/* import __COLOCATED_TEMPLATE__ from './ask-a-teammate.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type { FinSetupBehaviorSaveParams, HitlFrequency } from 'embercom/lib/operator/fin/types';
import type IntlService from 'ember-intl/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';

const FIN_EMAIL_HITL_FF = 'fin-email-hitl';
const DEFAULT_FREQUENCY: HitlFrequency = 'rarely';

const FREQUENCY_TRANSLATION_KEYS: Record<HitlFrequency, string> = {
  rarely: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.rarely',
  sometimes: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.sometimes',
  often: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.often',
};

const FREQUENCY_HELPER_KEYS: Record<HitlFrequency, string> = {
  rarely: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.helper-rarely',
  sometimes: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.helper-sometimes',
  often: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.frequency.helper-often',
};

type Option = 'on' | 'off';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (
    params: FinSetupBehaviorSaveParams,
    options?: {
      messages?: { success?: string; failure?: string };
      rethrow?: boolean;
    },
  ) => Promise<void> | void;
}

interface Signature {
  Args: Args;
}

export default class AskATeammate extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked selectedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  @tracked selectedFrequency: HitlFrequency =
    this.behavior.hitlSettings?.frequency ?? DEFAULT_FREQUENCY;
  @tracked isSaving = false;

  get frequencies(): { value: HitlFrequency; translationKey: string }[] {
    return (Object.keys(FREQUENCY_TRANSLATION_KEYS) as HitlFrequency[]).map((value) => ({
      value,
      translationKey: FREQUENCY_TRANSLATION_KEYS[value],
    }));
  }

  get frequencyHelperKey(): string {
    return FREQUENCY_HELPER_KEYS[this.selectedFrequency];
  }

  get behavior(): Profile {
    return this.args.ruleset.rulesetLinks.firstObject.object;
  }

  get canUseFinEmailHitl(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.(FIN_EMAIL_HITL_FF));
  }

  get options() {
    return [
      {
        value: 'off' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-off.description',
      },
      {
        value: 'on' as const,
        translationKey: 'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.label',
        subtitleTranslationKey:
          'operator.fin.setup.setup-and-go-live.ask-a-teammate.radio-on.description',
      },
    ];
  }

  get isEnabled(): boolean {
    return this.selectedOption === 'on';
  }

  get statusLabel(): string {
    return this.intl.t(
      this.isEnabled
        ? 'operator.fin.setup.setup-and-go-live.ask-a-teammate.enabled-status'
        : 'operator.fin.setup.setup-and-go-live.ask-a-teammate.disabled-status',
    );
  }

  get isInteractionDisabled(): boolean {
    return this.isSaving;
  }

  @action
  async setSelectedOption(option: Option) {
    if (this.isInteractionDisabled || option === this.selectedOption) {
      return;
    }

    let previousOption = this.selectedOption;
    this.selectedOption = option;
    this.isSaving = true;

    let hitlSettings: { enabled: boolean; frequency?: HitlFrequency } =
      option === 'on' ? { enabled: true, frequency: this.selectedFrequency } : { enabled: false };

    try {
      await this.args.partialSave(
        { hitl_settings: hitlSettings },
        {
          messages: {
            success: this.intl.t(
              'operator.fin.setup.setup-and-go-live.ask-a-teammate.save-success',
            ),
            failure: this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.save-error'),
          },
          rethrow: true,
        },
      );
    } catch {
      this.selectedOption = previousOption;
    } finally {
      this.isSaving = false;
    }
  }

  @action
  async setSelectedFrequency(frequency: HitlFrequency) {
    if (this.isInteractionDisabled || frequency === this.selectedFrequency) {
      return;
    }

    let previousFrequency = this.selectedFrequency;
    this.selectedFrequency = frequency;
    this.isSaving = true;

    try {
      await this.args.partialSave(
        { hitl_settings: { enabled: true, frequency } },
        {
          messages: {
            success: this.intl.t(
              'operator.fin.setup.setup-and-go-live.ask-a-teammate.save-success',
            ),
            failure: this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.save-error'),
          },
          rethrow: true,
        },
      );
    } catch {
      this.selectedFrequency = previousFrequency;
    } finally {
      this.isSaving = false;
    }
  }

  @action
  close(closeAccordion: () => void) {
    closeAccordion();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::AskATeammate': typeof AskATeammate;
    'operator/fin/setup/sections/ask-a-teammate': typeof AskATeammate;
  }
}
